import { Person } from '../schema/accountSchema'
/**
 * Enhanced people generator for realistic enterprise data
 * Generates diverse, professional contacts with proper roles and departments
 */
// Expanded name pools for diversity
const firstNames = [
  // Male names
  'James',
  'Michael',
  'David',
  'Robert',
  'John',
  'William',
  'Richard',
  'Thomas',
  'Christopher',
  'Daniel',
  'Matthew',
  'Andrew',
  'Joshua',
  'Brian',
  'Kevin',
  'Ryan',
  'Jason',
  'Justin',
  'Brandon',
  'Eric',
  'Benjamin',
  'Samuel',
  'Alexander',
  'Nathan',
  'Jacob',
  'Ethan',
  'Noah',
  'Lucas',
  'Mason',
  'Oliver',
  // Female names
  'Sarah',
  'Jennifer',
  'Emily',
  'Jessica',
  'Ashley',
  'Amanda',
  'Melissa',
  'Michelle',
  'Stephanie',
  'Nicole',
  'Elizabeth',
  'Rebecca',
  'Laura',
  'Rachel',
  'Lisa',
  'Karen',
  'Angela',
  'Maria',
  'Patricia',
  'Linda',
  'Emma',
  'Olivia',
  'Sophia',
  'Isabella',
  'Mia',
  'Charlotte',
  'Amelia',
  'Harper',
  'Evelyn',
  'Abigail',
  'Madison',
  'Samantha',
  'Victoria',
  'Grace',
  'Hannah',
  'Natalie',
  'Chloe',
  'Lily',
  'Zoe',
  'Ella',
]
const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Thompson',
  'White',
  'Harris',
  'Sanchez',
  'Clark',
  'Ramirez',
  'Lewis',
  'Robinson',
  'Walker',
  'Young',
  'Allen',
  'King',
  'Wright',
  'Scott',
  'Torres',
  'Nguyen',
  'Hill',
  'Flores',
  'Green',
  'Adams',
  'Nelson',
  'Baker',
  'Hall',
  'Rivera',
  'Campbell',
  'Mitchell',
  'Carter',
  'Roberts',
  'Gomez',
  'Phillips',
  'Evans',
  'Turner',
  'Diaz',
  'Parker',
  'Cruz',
  'Edwards',
  'Collins',
  'Reyes',
  'Stewart',
]
// Quad team configuration
const quadRoles = [
  'Account Director',
  'Client Success Manager',
  'Creative Strategist',
  'Print Operations Lead',
  'Data Analyst',
  'Solutions Engineer',
  'Finance Partner',
  'Production Manager',
  'Account Coordinator',
  'Technical Lead',
]
const quadFunctions: Array<
  | 'Marketing'
  | 'Procurement'
  | 'IT'
  | 'Finance'
  | 'Operations'
  | 'Legal'
  | 'Executive'
  | 'Agency'
> = ['Operations', 'Marketing', 'Finance', 'IT', 'Operations']
const quadDepartments = [
  'Client Services',
  'Creative',
  'Operations',
  'Analytics',
  'Finance',
  'Strategy',
  'Production',
  'Technology',
]
const quadLocations = [
  'Chicago, IL',
  'Milwaukee, WI',
  'Dallas, TX',
  'Atlanta, GA',
  'Boston, MA',
  'Minneapolis, MN',
  'New York, NY',
  'Remote',
]
const quadTags = [
  'Internal Lead',
  'Account Manager',
  'Data Liaison',
  'Creative Owner',
  'Technical Expert',
  'Operations Lead',
  'Client Partner',
]
// Client team configuration
const clientRoles = [
  'Marketing Director',
  'Brand Manager',
  'Procurement Lead',
  'Media Planner',
  'Product Manager',
  'Communications Specialist',
  'VP of Marketing',
  'Digital Marketing Manager',
  'Category Manager',
  'Supply Chain Director',
  'Creative Director',
  'Marketing Operations Manager',
]
const clientFunctions: Array<
  | 'Marketing'
  | 'Procurement'
  | 'IT'
  | 'Finance'
  | 'Operations'
  | 'Legal'
  | 'Executive'
  | 'Agency'
> = [
  'Marketing',
  'Procurement',
  'Marketing',
  'Marketing',
  'Operations',
  'Marketing',
  'Executive',
]
const clientDepartments = [
  'Marketing',
  'Procurement',
  'Media',
  'Product',
  'Branding',
  'Strategy',
  'Communications',
  'Operations',
]
const clientLocations = [
  'New York, NY',
  'Los Angeles, CA',
  'London, UK',
  'Toronto, ON',
  'São Paulo, Brazil',
  'Berlin, Germany',
  'Chicago, IL',
  'San Francisco, CA',
  'Seattle, WA',
  'Austin, TX',
]
const clientTags = [
  'Decision Maker',
  'Finance Gatekeeper',
  'Creative Reviewer',
  'Contract Approver',
  'Budget Owner',
  'Primary Contact',
  'Technical Contact',
]
// Pool of recurring Quad staff (realistic for account managers to handle multiple accounts)
const recurringQuadStaff: Person[] = []
function randomItem<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}
function generatePhone(): string {
  const area = 312 + Math.floor(Math.random() * 100)
  const prefix = 555
  const line = 1000 + Math.floor(Math.random() * 9000)
  return `(${area}) ${prefix}-${line}`
}
function generateLastTouch(): string {
  const daysAgo = Math.floor(Math.random() * 14) + 1
  const date = new Date()
  date.setDate(date.getDate() - daysAgo)
  return date.toISOString()
}
function generateUniqueName(usedNames: Set<string>): {
  firstName: string
  lastName: string
  fullName: string
} {
  let attempts = 0
  while (attempts < 100) {
    const firstName = randomItem(firstNames)
    const lastName = randomItem(lastNames)
    const fullName = `${firstName} ${lastName}`
    if (!usedNames.has(fullName)) {
      usedNames.add(fullName)
      return { firstName, lastName, fullName }
    }
    attempts++
  }
  // Fallback with number suffix if we can't find unique name
  const firstName = randomItem(firstNames)
  const lastName = randomItem(lastNames)
  const fullName = `${firstName} ${lastName} ${Math.floor(Math.random() * 1000)}`
  usedNames.add(fullName)
  return { firstName, lastName, fullName }
}
function generateQuadPerson(
  index: number,
  accountId: string,
  usedNames: Set<string>,
  allowRecurring: boolean = true,
): Person {
  // 30% chance to use recurring staff for realism
  if (allowRecurring && recurringQuadStaff.length > 0 && Math.random() < 0.3) {
    return recurringQuadStaff[
      Math.floor(Math.random() * recurringQuadStaff.length)
    ]
  }
  const { firstName, lastName, fullName } = generateUniqueName(usedNames)
  const roleIndex = index % quadRoles.length
  const role_title = quadRoles[roleIndex]
  const func = quadFunctions[index % quadFunctions.length]
  const department = quadDepartments[index % quadDepartments.length]
  const location = randomItem(quadLocations)
  const tags = [randomItem(quadTags)]
  if (Math.random() > 0.5) {
    tags.push(randomItem(quadTags))
  }
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@quad.com`
  const person: Person = {
    id: `quad-${accountId}-${index}`,
    name: fullName,
    email,
    phone: generatePhone(),
    role_title,
    function: func,
    side: 'Quad',
    department,
    location,
    tags: Array.from(new Set(tags)),
    last_touch: generateLastTouch(),
    organization: 'Quad',
    role: role_title,
  }
  // Add to recurring pool occasionally
  if (recurringQuadStaff.length < 20 && Math.random() < 0.2) {
    recurringQuadStaff.push(person)
  }
  return person
}
function generateClientPerson(
  index: number,
  accountId: string,
  accountName: string,
  usedNames: Set<string>,
): Person {
  const { firstName, lastName, fullName } = generateUniqueName(usedNames)
  const roleIndex = index % clientRoles.length
  const role_title = clientRoles[roleIndex]
  const func = clientFunctions[index % clientFunctions.length]
  const department = clientDepartments[index % clientDepartments.length]
  const location = randomItem(clientLocations)
  const tags = [randomItem(clientTags)]
  if (Math.random() > 0.6) {
    tags.push(randomItem(clientTags))
  }
  // Generate domain from account name
  const domain =
    accountName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '')
      .substring(0, 20) + '.com'
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${domain}`
  return {
    id: `client-${accountId}-${index}`,
    name: fullName,
    email,
    phone: generatePhone(),
    role_title,
    function: func,
    side: 'Client',
    department,
    location,
    tags: Array.from(new Set(tags)),
    last_touch: generateLastTouch(),
    organization: 'Client',
    role: role_title,
  }
}
export function generateAccountPeople(
  accountId: string,
  accountName: string,
): { client_contacts: Person[]; quad_team: Person[] } {
  const usedNames = new Set<string>()
  const client_contacts: Person[] = []
  const quad_team: Person[] = []
  // Generate 12-15 client contacts for enterprise accounts
  const clientCount = 12 + Math.floor(Math.random() * 4)
  for (let i = 0; i < clientCount; i++) {
    client_contacts.push(
      generateClientPerson(i, accountId, accountName, usedNames),
    )
  }
  // Generate 8-10 Quad team members
  const quadCount = 8 + Math.floor(Math.random() * 3)
  for (let i = 0; i < quadCount; i++) {
    quad_team.push(generateQuadPerson(i, accountId, usedNames, true))
  }
  return { client_contacts, quad_team }
}
